import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Loader2, AlertCircle } from 'lucide-react';
import axios from '../config/axios';

const StripeComplete = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [accountInfo, setAccountInfo] = useState(null);

  useEffect(() => {
    const stripeStatus = searchParams.get('stripe');
    
    // Check account status via API
    const checkStatus = async () => {
      try {
        const token = localStorage.getItem('access_token');
        if (!token) {
          setStatus('not_logged_in');
          return;
        }

        const response = await axios.get('/api/monetization/stripe/account-status', {
          headers: { Authorization: `Bearer ${token}` }
        });

        console.log('[STRIPE COMPLETE] Account status:', response.data);
        setAccountInfo(response.data);

        if (response.data.details_submitted) {
          setStatus('success');
        } else if (response.data.connected) {
          setStatus('incomplete');
        } else {
          setStatus('not_connected');
        }
      } catch (error) {
        console.error('[STRIPE COMPLETE] Error checking status:', error);
        setStatus('error');
      }
    };

    // If we have a status from URL params, use it initially
    if (stripeStatus === 'success') {
      checkStatus();
    } else if (stripeStatus === 'incomplete') {
      // Use a delayed state update to avoid synchronous setState
      const timer = setTimeout(() => setStatus('incomplete'), 0);
      return () => clearTimeout(timer);
    } else if (stripeStatus === 'error') {
      const timer = setTimeout(() => setStatus('error'), 0);
      return () => clearTimeout(timer);
    } else {
      checkStatus();
    }
  }, [searchParams]);

  const handleGoToDashboard = () => {
    navigate('/reviewer-dashboard');
  };

  const handleRetrySetup = async () => {
    try {
      setStatus('loading');
      const token = localStorage.getItem('access_token');
      const response = await axios.post('/api/monetization/stripe/create-account-link', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('[STRIPE COMPLETE] Retry response:', response.data);

      if (response.data.success && response.data.url) {
        window.location.href = response.data.url;
      } else {
        setStatus('error');
      }
    } catch (error) {
      console.error('[STRIPE COMPLETE] Retry error:', error);
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-gray-800 border-gray-700">
        <CardHeader className="text-center">
          <CardTitle className="text-white text-2xl">Stripe Connect Setup</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {status === 'loading' && (
            <div className="text-center py-8">
              <Loader2 className="w-16 h-16 text-yellow-500 animate-spin mx-auto mb-4" />
              <p className="text-gray-300">Verifying your Stripe account...</p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center py-8">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">Setup Complete!</h3>
              <p className="text-gray-300 mb-6">
                Your Stripe account is connected and ready to receive payments.
              </p>
              {accountInfo && (
                <div className="bg-gray-700 rounded-lg p-4 mb-6 text-left">
                  <p className="text-sm text-gray-400">Account Status:</p>
                  <p className="text-white">
                    ✅ Charges Enabled: {accountInfo.charges_enabled ? 'Yes' : 'Pending'}
                  </p>
                  <p className="text-white">
                    ✅ Payouts Enabled: {accountInfo.payouts_enabled ? 'Yes' : 'Pending'}
                  </p>
                </div>
              )}
              <Button onClick={handleGoToDashboard} className="bg-yellow-500 text-black hover:bg-yellow-600">
                Go to Dashboard
              </Button>
            </div>
          )}

          {status === 'incomplete' && (
            <div className="text-center py-8">
              <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">Setup Incomplete</h3>
              <p className="text-gray-300 mb-6">
                Please complete the Stripe onboarding process to start receiving payments.
              </p>
              <div className="space-y-3">
                <Button onClick={handleRetrySetup} className="w-full bg-yellow-500 text-black hover:bg-yellow-600">
                  Continue Setup
                </Button>
                <Button onClick={handleGoToDashboard} variant="outline" className="w-full">
                  Go to Dashboard
                </Button>
              </div>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center py-8">
              <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">Something Went Wrong</h3>
              <p className="text-gray-300 mb-6">
                There was an issue with your Stripe setup. Please try again.
              </p>
              <div className="space-y-3">
                <Button onClick={handleRetrySetup} className="w-full bg-yellow-500 text-black hover:bg-yellow-600">
                  Try Again
                </Button>
                <Button onClick={handleGoToDashboard} variant="outline" className="w-full">
                  Go to Dashboard
                </Button>
              </div>
            </div>
          )}

          {status === 'not_logged_in' && (
            <div className="text-center py-8">
              <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">Please Log In</h3>
              <p className="text-gray-300 mb-6">
                You need to be logged in to complete Stripe setup.
              </p>
              <Button onClick={() => navigate('/login')} className="bg-yellow-500 text-black hover:bg-yellow-600">
                Go to Login
              </Button>
            </div>
          )}

          {status === 'not_connected' && (
            <div className="text-center py-8">
              <AlertCircle className="w-16 h-16 text-gray-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">No Stripe Account</h3>
              <p className="text-gray-300 mb-6">
                Start the Stripe setup process from your dashboard.
              </p>
              <Button onClick={handleGoToDashboard} className="bg-yellow-500 text-black hover:bg-yellow-600">
                Go to Dashboard
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default StripeComplete;
